import { useState } from 'react';
import { NavArrowRight } from 'iconoir-react';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  SettingsButton,
  SettingsDrawer,
  SettingsGroup,
  SettingsItem,
} from 'components/blocks/settings-drawer';
import { ViewOnlyBadge } from 'components/blocks/view-only-badge';
import { BUDDY_CONFIG } from 'config/buddy/config';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Textarea } from 'components/ui/textarea';
import { AgentSoulSection } from 'features/agent-soul/components/agent-soul-section';
import { isAgentSoulHidden, useAgentSoul } from 'features/agent-soul/hooks/use-agent-soul';
import type { Agent } from 'features/chat/agent-chat-module/types/agent.service.type';
import { useAgentConfigSettings } from 'features/chat/hooks/use-agent-config-settings';
import { useGetAgentById } from 'features/chat/hooks/use-agents';
import { useDebouncedAgentSave } from 'features/chat/hooks/use-debounced-agent-save';
import {
  getAgentPersonaValues,
  useSaveAgentPersona,
} from 'features/chat/hooks/use-save-agent-persona';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';
import { canEditAgentConfig, isAgentOwnerAccess } from 'pages/agents/utils/agent-share-access';
import { ScheduledJobList } from 'pages/scheduler/components/scheduled-job-list';
import { useGetScheduledJobs } from 'pages/scheduler/hooks/use-scheduled-jobs';
import { LIVE_STATUSES } from 'pages/scheduler/services/scheduled-jobs.types';
import { LearnedSkillsPanel } from 'pages/skills/learned-skills-panel';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { DEFAULT_MAIN_CHAT_AGENT_ID, isCatalogueAgentId } from 'utils/constants/chat-constants';
import {
  RESOURCE_VISIBILITY,
  normalizeResourceVisibility,
  type ResourceVisibility,
} from 'types/visibility';
import { AgentConfigSettings, AgentSkillSettings } from './agent-config-settings';

const labelClassName = 'text-sm font-medium leading-body text-grey-900 dark:text-foreground';

const fieldClassName =
  'w-full rounded-field border border-grey-100 bg-white px-5 py-4 text-base font-normal leading-body text-black shadow-none placeholder:text-grey-300 focus-visible:ring-1 focus-visible:ring-ring dark:border-border dark:bg-card dark:text-foreground';

const errorClassName = 'text-xs font-medium text-destructive';

const radioClassName =
  'h-[18px] w-[18px] shrink-0 rounded-full border border-grey-500 bg-white shadow-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 data-[state=checked]:border-[5px] data-[state=checked]:border-black dark:bg-card dark:data-[state=checked]:border-foreground [&_svg]:hidden';

// Figma: pill tabs; the active one is filled grey. Tabs share the row by label
// length (flex-auto) so longer labels like German "Fähigkeiten" still fit.
// `white` turns dark in dark mode, so the dark fills use the light sidebar pill tokens.
const tabListClassName =
  'h-auto w-full justify-center gap-2 rounded-none border-0 bg-transparent p-0';

const tabTriggerClassName =
  'h-10 flex-auto rounded-full bg-transparent px-3 text-base font-medium leading-heading text-grey-900 shadow-none hover:bg-grey-50 hover:text-grey-900 data-[state=active]:bg-grey-100 data-[state=active]:text-grey-900 data-[state=active]:shadow-none dark:text-foreground dark:hover:bg-sidebar-pill-dark dark:hover:text-foreground dark:data-[state=active]:bg-sidebar-pill-dark-hover dark:data-[state=active]:text-foreground';

// The routine tab fills the drawer so its bordered box reaches the bottom.
// Radix keeps an inactive tab panel in the DOM with only a `hidden` attribute,
// which `flex` overrides, so it must be hidden explicitly or it leaves a gap.
const tabsClassName = 'flex flex-1 flex-col gap-5';
const routineContentClassName = 'mt-0 flex flex-1 flex-col data-[state=inactive]:hidden';
// Edit and Skills sit in the same bordered panel as the Routine list.
const panelContentClassName =
  'mt-0 flex flex-1 flex-col gap-6 rounded-lg border border-grey-100 p-4 dark:border-border data-[state=inactive]:hidden';

const radioLabelClassName =
  'cursor-pointer text-base font-normal leading-body text-grey-900 dark:text-foreground';

type AgentSettingsProps = {
  agentId: string | null;
};

/** Gear button in the chat header. Buddy (the default agent) gets the Routines tab, his read-only
 *  Soul tab when souls are on, and learned Skills; other agents also get Edit. */
export function AgentSettings({ agentId }: AgentSettingsProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const userId = useCommonStore((state) => state.user?.id);
  // No agent, or the default ID, means Buddy is answering. Buddy shares the
  // default main-chat ID, so isCatalogueAgentId is false for Buddy too.
  const isBuddy = !isCatalogueAgentId(agentId);
  const { data: agentData } = useGetAgentById({ id: agentId ?? '' }, { enabled: !isBuddy });
  const agent = isBuddy ? undefined : agentData?.agent;
  // Buddy's soul belongs to the default main-chat agent; read once the sheet opens.
  const buddySoul = useAgentSoul(isBuddy && isOpen ? DEFAULT_MAIN_CHAT_AGENT_ID : null);
  const showBuddySoul =
    !isAgentSoulHidden(buddySoul.data, buddySoul.error) && (!!buddySoul.data || buddySoul.isError);

  if (!isBuddy && !agent) return null;

  // Same rules as the agent config page: owners and share editors can edit,
  // and only the owner can change visibility.
  const canEdit = !!agent && canEditAgentConfig(agent, userId);
  const isOwner = !!agent && isAgentOwnerAccess(agent, userId);

  return (
    <>
      <SettingsButton isActive={isOpen} onClick={() => setIsOpen(true)} />

      <SettingsDrawer
        open={isOpen}
        onOpenChange={setIsOpen}
        title={t('AGENT_SETTINGS', { defaultValue: 'Agent settings' })}
        badge={
          !agent || canEdit ? null : (
            <ViewOnlyBadge
              compact
              description={t('YOU_ARE_NOT_THE_OWNER_OF_THIS_AGENT', {
                defaultValue: 'You are not the owner of this agent.',
              })}
            />
          )
        }
      >
        {/* Buddy has routines, a read-only soul and learned skills, not standing skills or Edit. */}
        {!agent ? (
          <Tabs defaultValue="routine" className={tabsClassName}>
            <TabsList className={tabListClassName}>
              <TabsTrigger value="routine" className={tabTriggerClassName}>
                {t('ROUTINES', { defaultValue: 'Routines' })}
              </TabsTrigger>
              {showBuddySoul ? (
                <TabsTrigger value="soul" className={tabTriggerClassName}>
                  {t('AGENT_SOUL_TITLE', { defaultValue: 'Soul' })}
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="skills" className={tabTriggerClassName}>
                {t('SKILLS', { defaultValue: 'Skills' })}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="routine" className={routineContentClassName}>
              <AgentRoutineSettings agentId={BUDDY_CONFIG.agentId} />
            </TabsContent>
            {showBuddySoul ? (
              <TabsContent value="soul" className={panelContentClassName}>
                {/* Buddy's soul is read-only: the section shows it with the Buddy note. */}
                <AgentSoulSection agentId={DEFAULT_MAIN_CHAT_AGENT_ID} canEdit={false} />
              </TabsContent>
            ) : null}
            <TabsContent value="skills" className={panelContentClassName}>
              <LearnedSkillsPanel
                agentId={BUDDY_CONFIG.agentId}
                agentName={t('BUDDY', { defaultValue: 'Buddy' })}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <AgentSettingsTabs
            key={agent.id}
            agent={agent}
            projectKey={projectKey}
            canEdit={canEdit}
            isOwner={isOwner}
          />
        )}
      </SettingsDrawer>
    </>
  );
}

type AgentSettingsTabsProps = {
  agent: Agent;
  projectKey: string;
  canEdit: boolean;
  isOwner: boolean;
};

/** Edit, Routine and Skills tabs. Edit and Skills share one config state, so neither saves an old value. */
function AgentSettingsTabs({ agent, projectKey, canEdit, isOwner }: AgentSettingsTabsProps) {
  const { t } = useTranslation();
  const config = useAgentConfigSettings(agent, projectKey);

  return (
    <Tabs defaultValue="edit" className={tabsClassName}>
      <TabsList className={tabListClassName}>
        <TabsTrigger value="edit" className={tabTriggerClassName}>
          {t('EDIT', { defaultValue: 'Edit' })}
        </TabsTrigger>
        <TabsTrigger value="routine" className={tabTriggerClassName}>
          {t('ROUTINES', { defaultValue: 'Routines' })}
        </TabsTrigger>
        <TabsTrigger value="skills" className={tabTriggerClassName}>
          {t('SKILLS', { defaultValue: 'Skills' })}
        </TabsTrigger>
      </TabsList>

      {/* Kept mounted so the name and description fields keep what was typed. */}
      <TabsContent value="edit" forceMount className={panelContentClassName}>
        <AgentProfileSettings
          agent={agent}
          projectKey={projectKey}
          disabled={!canEdit}
          canChangeVisibility={isOwner}
        />

        <AgentConfigSettings agent={agent} projectKey={projectKey} config={config} />

        {/* Own key: the shared ADVANCED key reads "More" in the left sidebar. */}
        <Link
          to={`/agent-details/${agent.id}/configuration`}
          className="group flex w-full items-center gap-3 rounded-lg border border-grey-100 p-4 transition-colors hover:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-border dark:hover:bg-sidebar-pill-dark"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-grey-50 text-grey-900 dark:bg-sidebar-pill-dark-hover dark:text-foreground">
            <SlidersHorizontal width={20} height={20} strokeWidth={1.5} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-sm font-semibold leading-heading text-grey-900 dark:text-foreground">
              {t('AGENT_SETTINGS_ADVANCED', { defaultValue: 'Advanced' })}
            </span>
            <span className="text-xs font-medium leading-body text-grey-500 dark:text-muted-foreground">
              {t('AGENT_SETTINGS_ADVANCED_DESCRIPTION', {
                defaultValue: 'Subagents, guardrails and more.',
              })}
            </span>
          </span>
          <NavArrowRight
            width={20}
            height={20}
            className="shrink-0 text-grey-500 transition-transform group-hover:translate-x-0.5"
          />
        </Link>
      </TabsContent>

      <TabsContent value="routine" className={routineContentClassName}>
        <AgentRoutineSettings agentId={agent.id} />
      </TabsContent>

      <TabsContent value="skills" className={panelContentClassName}>
        <AgentSkillSettings agent={agent} config={config} />
      </TabsContent>
    </Tabs>
  );
}

/** This agent's active and paused scheduled jobs, newest first. */
function AgentRoutineSettings({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetScheduledJobs({
    agentId,
    status: LIVE_STATUSES,
    skip: 0,
    take: 25,
    sort: 'createdDate',
    order: 'desc',
  });

  return (
    <div className="flex flex-1 flex-col gap-3.5 rounded-lg border border-grey-100 p-4 dark:border-border">
      <p className="text-sm font-medium leading-body text-grey-400 dark:text-muted-foreground">
        {t('ROUTINES', { defaultValue: 'Routines' })}
      </p>
      <ScheduledJobList
        compact
        jobs={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        emptyMessage={t('NO_SCHEDULED_JOBS', { defaultValue: 'No scheduled jobs.' })}
      />
    </div>
  );
}

type AgentProfileSettingsProps = {
  agent: Agent;
  projectKey: string;
  disabled: boolean;
  canChangeVisibility: boolean;
};

/**
 * Name, description and visibility. They share one save so a visibility change
 * never sends an old name. Text saves once typing pauses; visibility saves at once.
 */
function AgentProfileSettings({
  agent,
  projectKey,
  disabled,
  canChangeVisibility,
}: AgentProfileSettingsProps) {
  const { t } = useTranslation();
  const saveAgentPersona = useSaveAgentPersona(projectKey);
  const [name, setName] = useState(agent.name || '');
  const [description, setDescription] = useState(agent.description || '');
  const [visibility, setVisibility] = useState(normalizeResourceVisibility(agent.visibility));

  const saveLater = useDebouncedAgentSave(
    async (nextName: string, nextDescription: string, nextVisibility: ResourceVisibility) => {
      // Both fields are required, so a blank one is not saved.
      if (!nextName.trim() || !nextDescription.trim()) return;

      await saveAgentPersona(agent, {
        ...getAgentPersonaValues(agent),
        name: nextName,
        description: nextDescription,
        visibility: nextVisibility,
      });
    }
  );

  const handleNameChange = (value: string) => {
    setName(value);
    saveLater(value, description, visibility);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    saveLater(name, value, visibility);
  };

  const handleVisibilityChange = (value: string) => {
    const nextVisibility = normalizeResourceVisibility(value);
    setVisibility(nextVisibility);
    saveLater(name, description, nextVisibility);
    saveLater.flush();
  };

  return (
    <>
      <SettingsGroup className="gap-6">
        <div className="flex flex-col gap-1">
          <Label htmlFor="agent-settings-name" className={labelClassName}>
            {t('AGENT_NAME', { defaultValue: 'Agent Name' })}
            <span className="text-error-500">*</span>
          </Label>
          <Input
            id="agent-settings-name"
            value={name}
            onChange={(event) => handleNameChange(event.target.value)}
            disabled={disabled}
            placeholder={t('AGENT_NAME_PLACEHOLDER', {
              defaultValue: 'e.g. Customer Support Agent',
            })}
            className={cn('!h-12', fieldClassName)}
          />
          {!disabled && !name.trim() && (
            <p className={errorClassName}>
              {t('AGENT_NAME_REQUIRED', { defaultValue: 'Agent name is required' })}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="agent-settings-description" className={labelClassName}>
            {t('DESCRIPTION', { defaultValue: 'Description' })}
            <span className="text-error-500">*</span>
          </Label>
          <Textarea
            id="agent-settings-description"
            rows={4}
            value={description}
            onChange={(event) => handleDescriptionChange(event.target.value)}
            disabled={disabled}
            placeholder={t('BRIEFLY_DESCRIBE_AI_AGENT', {
              defaultValue: 'Briefly describe the AI agent you want to create',
            })}
            className={cn('min-h-[136px] resize-none', fieldClassName)}
          />
          {!disabled && !description.trim() && (
            <p className={errorClassName}>
              {t('DESCRIPTION_REQUIRED', { defaultValue: 'Description is required' })}
            </p>
          )}
        </div>
      </SettingsGroup>

      <SettingsGroup>
        <SettingsItem title={t('VISIBILITY', { defaultValue: 'Visibility' })} />
        <RadioGroup
          value={visibility}
          onValueChange={handleVisibilityChange}
          disabled={!canChangeVisibility}
          className="flex flex-wrap gap-8"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value={RESOURCE_VISIBILITY.PRIVATE}
              id="agent-settings-only-me"
              className={radioClassName}
            />
            <Label htmlFor="agent-settings-only-me" className={radioLabelClassName}>
              {t('ONLY_ME', { defaultValue: 'Only Me' })}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value={RESOURCE_VISIBILITY.PUBLIC}
              id="agent-settings-everyone"
              className={radioClassName}
            />
            <Label htmlFor="agent-settings-everyone" className={radioLabelClassName}>
              {t('EVERYONE', { defaultValue: 'Everyone' })}
            </Label>
          </div>
        </RadioGroup>
      </SettingsGroup>
    </>
  );
}
